import { db } from './chunk-db';
import logger from '../utils/logger';

export interface BackupData {
  version: number;
  timestamp: number;
  chunks: any[];
  uploads: any[];
}

export interface MigrationCheckpoint {
  stage: 'backup' | 'copy_chunks' | 'copy_uploads' | 'verify' | 'complete';
  timestamp: number;
  recordsProcessed: number;
  totalRecords: number;
  checksums: {
    chunks?: string;
    uploads?: string;
  };
}

export async function createBackup(): Promise<BackupData> {
  logger.info('Starting pre-migration backup...');
  
  const chunks = await db.chunks.toArray();
  const uploads = await db.uploads.toArray();
  
  const backup: BackupData = {
    version: 4,
    timestamp: Date.now(),
    chunks,
    uploads,
  };
  
  logger.info('Backup created:', {
    chunks: chunks.length,
    uploads: uploads.length,
    timestamp: backup.timestamp,
  });
  
  return backup;
}

export function exportBackupToJSON(backup: BackupData): string {
  return JSON.stringify(backup, null, 2);
}

export function downloadBackup(backup: BackupData): void {
  const json = exportBackupToJSON(backup);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `dadan-backup-v4-${backup.timestamp}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function importBackupFromJSON(json: string): Promise<BackupData> {
  try {
    const backup: BackupData = JSON.parse(json);
    
    if (!backup.version || !backup.chunks || !backup.uploads) {
      throw new Error('Invalid backup format');
    }
    
    return backup;
  } catch (error) {
    logger.error('Failed to import backup:', error);
    throw error;
  }
}

export async function restoreFromBackup(backup: BackupData): Promise<void> {
  logger.info('Starting backup restore...');
  
  await db.chunks.clear();
  await db.uploads.clear();
  
  await db.chunks.bulkAdd(backup.chunks);
  await db.uploads.bulkAdd(backup.uploads);
  
  logger.info('Backup restored successfully:', {
    chunks: backup.chunks.length,
    uploads: backup.uploads.length,
  });
}

export function calculateChecksum(data: any[]): string {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

export async function verifyDataIntegrity(
  v4Chunks: any[],
  v5Chunks: any[],
  v4Uploads: any[],
  v5Uploads: any[]
): Promise<{ valid: boolean; errors: string[] }> {
  const errors: string[] = [];
  
  if (v4Chunks.length !== v5Chunks.length) {
    errors.push(`Chunk count mismatch: v4=${v4Chunks.length}, v5=${v5Chunks.length}`);
  }
  
  if (v4Uploads.length !== v5Uploads.length) {
    errors.push(`Upload count mismatch: v4=${v4Uploads.length}, v5=${v5Uploads.length}`);
  }
  
  const v4ChunksChecksum = calculateChecksum(v4Chunks);
  const v5ChunksChecksum = calculateChecksum(
    v5Chunks.map(c => ({ ...c, templateId: undefined, sceneId: undefined }))
  );
  
  if (v4ChunksChecksum !== v5ChunksChecksum) {
    errors.push(`Chunks checksum mismatch: v4=${v4ChunksChecksum}, v5=${v5ChunksChecksum}`);
  }
  
  const v4UploadsChecksum = calculateChecksum(v4Uploads);
  const v5UploadsChecksum = calculateChecksum(
    v5Uploads.map(u => ({ 
      ...u, 
      thumbnails: undefined, 
      syncQueued: undefined, 
      templateId: undefined 
    }))
  );
  
  if (v4UploadsChecksum !== v5UploadsChecksum) {
    errors.push(`Uploads checksum mismatch: v4=${v4UploadsChecksum}, v5=${v5UploadsChecksum}`);
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

export function createMigrationCheckpoint(
  stage: MigrationCheckpoint['stage'],
  recordsProcessed: number,
  totalRecords: number,
  checksums?: { chunks?: string; uploads?: string }
): MigrationCheckpoint {
  return {
    stage,
    timestamp: Date.now(),
    recordsProcessed,
    totalRecords,
    checksums: checksums || {},
  };
}

export function saveMigrationCheckpoint(checkpoint: MigrationCheckpoint): void {
  localStorage.setItem('dadan_migration_checkpoint', JSON.stringify(checkpoint));
}

export function loadMigrationCheckpoint(): MigrationCheckpoint | null {
  try {
    const stored = localStorage.getItem('dadan_migration_checkpoint');
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function clearMigrationCheckpoint(): void {
  localStorage.removeItem('dadan_migration_checkpoint');
}
